import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import ShareBox from '../../components/ShareBox';
import { 
  TrendingUp, TrendingDown, Shield, Target, ArrowRight, ArrowLeft,
  ChevronRight, Zap, AlertTriangle, CheckCircle, Clock, XCircle, 
  BookOpen, BarChart3, Activity, DollarSign, LineChart, Scale, Repeat
} from 'lucide-react';

const faqData = [
  {
    question: "Futures trading nedir?",
    answer: "Futures (vadeli islem), bir varligin gelecekteki fiyati uzerine yapilan sozlesme bazli islemdir. Kaldirac kullanarak hem yukselis hem dusus yonunde pozisyon acilabilir."
  },
  {
    question: "Spot ve futures farki nedir?",
    answer: "Spot piyasada varlik dogrudan alinir/satilir. Futures piyasada sozlesme ticareti yapilir, kaldirac kullanilabilir ve short pozisyon acilabilir."
  },
  {
    question: "Kaldirac ne demek?",
    answer: "Kaldirac (leverage), mevcut sermayenin kati kadar pozisyon acmayi saglar. 10x kaldirac ile $100 sermaye, $1000'lik pozisyon acar."
  },
  {
    question: "Likidasyon nedir?",
    answer: "Kaldiracli pozisyonda zarar teminat miktarina esitlendiginde pozisyonun zorla kapatilmasidir. Sermayenin tamami kaybedilir."
  },
  {
    question: "Futures trading guvenli midir?",
    answer: "Hayir. Kaldiracli islemler yuksek risk icerir. Sermayenin tamami kaybedilebilir. Deneyimsiz kullanicilar icin cok risklidir."
  }
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Futures Trading Nedir? Kripto Vadeli Islemler Rehberi",
    "description": "Futures trading nedir, nasil calisir? Kripto vadeli islemlerde kaldirac, likidasyon, long/short ve risk yonetimi. Yeni baslayanlar icin kapsamli rehber.",
    "author": { "@type": "Organization", "name": "Borsamex" },
    "publisher": { "@type": "Organization", "name": "Borsamex", "logo": { "@type": "ImageObject", "url": "https://borsamex.com/logo512.png" } },
    "datePublished": "2026-04-02",
    "dateModified": "2026-04-02",
    "mainEntityOfPage": "https://borsamex.com/rehber/futures-trading-nedir"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  }
];

const FuturesTradingNedirPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="Futures Trading Nedir? Kripto Vadeli Islemler Rehberi | Borsamex"
        description="Futures trading nedir, nasil calisir? Kripto vadeli islemlerde kaldirac, likidasyon, long/short ve risk yonetimi. Yeni baslayanlar icin kapsamli rehber."
        keywords="futures trading nedir, vadeli islem nedir, kripto futures, kaldirac nedir, likidasyon nedir, perpetual futures, kripto vadeli islem"
        canonical="https://borsamex.com/rehber/futures-trading-nedir"
        structuredData={structuredData}
      />

      <div className="border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Ana Sayfa</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/rehber" className="hover:text-white transition-colors">Rehber</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">Futures Trading Nedir?</span>
          </div>
        </div>
      </div>

      <article className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          
          <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">Trading</Badge>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="h-4 w-4" />
                <span>10 dk okuma</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Futures Trading Nedir? Kripto Vadeli Islemler
            </h1>
            
            <div className="p-6 bg-slate-900/50 border border-slate-700/50 rounded-xl">
              <p className="text-lg text-slate-300 leading-relaxed">
                <strong className="text-purple-400">Futures trading</strong> (vadeli islem), bir varligin gelecekteki fiyati uzerine 
                sozlesme bazli islem yapmayi ifade eder. <strong className="text-green-400">Kaldirac</strong> kullanarak hem yukselis (long) 
                hem dusus (short) yonunde pozisyon acilabilir.
              </p>
              <p className="text-slate-400 mt-4">
                Kripto piyasasinda en cok kullanilan futures turu perpetual (surekli) sozlesmelerdir.
              </p>
            </div>
          </motion.header>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="prose prose-invert prose-slate max-w-none">
            
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <LineChart className="h-4 w-4 text-purple-400" />
                </div>
                Futures Trading Nasil Calisir?
              </h2>
              
              <p className="text-slate-300 mb-4">
                Futures islemde varligin kendisini degil, fiyat sozlesmesini alir/satarsiniz. Bu yapi su avantajlari saglar:
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Kaldirac</strong> - Az sermaye ile buyuk pozisyon acilabilir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Short imkani</strong> - Dusus trendlerinden de kazanc elde edilebilir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Hedging</strong> - Spot portfoyu korumak icin karsi pozisyon acilabilir</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Scale className="h-4 w-4 text-blue-400" />
                </div>
                Spot vs Futures Karsilastirma
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Ozellik</th>
                      <th className="text-left py-3 px-4 text-green-400 font-medium">Spot</th>
                      <th className="text-left py-3 px-4 text-purple-400 font-medium">Futures</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-800">
                      <td className="py-3 px-4 text-slate-300">Varlik sahipligi</td>
                      <td className="py-3 px-4 text-green-400">Evet</td>
                      <td className="py-3 px-4 text-purple-400">Hayir (sozlesme)</td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-3 px-4 text-slate-300">Kaldirac</td>
                      <td className="py-3 px-4 text-green-400">Yok</td>
                      <td className="py-3 px-4 text-purple-400">1x - 125x</td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-3 px-4 text-slate-300">Short imkani</td>
                      <td className="py-3 px-4 text-green-400">Hayir</td>
                      <td className="py-3 px-4 text-purple-400">Evet</td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-3 px-4 text-slate-300">Risk seviyesi</td>
                      <td className="py-3 px-4 text-green-400">Orta</td>
                      <td className="py-3 px-4 text-red-400">Yuksek</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-slate-300">Likidasyon riski</td>
                      <td className="py-3 px-4 text-green-400">Yok</td>
                      <td className="py-3 px-4 text-red-400">Var</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Inline AI Sinyal Kutusu */}
            <section className="mb-12">
              <Card className="border-brand-green/30 bg-brand-green/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="h-6 w-6 text-brand-green" />
                    <span className="font-bold text-white">AI Sinyal Ornegi</span>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">SOL-USDT</span>
                      <Badge className="bg-green-500/20 text-green-400">LONG</Badge>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">
                      Giris: $178.50 | TP1: $185 | TP2: $195 | Stop: $173
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Activity className="h-3 w-3" />
                      <span>Futures sinyalleri AI tarafindan uretilir — manuel analiz gerektirmez</span>
                    </div>
                  </div>
                  <Link to="/signals">
                    <Button variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10 w-full">
                      Sinyalleri Gor <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <DollarSign className="h-4 w-4 text-amber-400" />
                </div>
                Kaldirac (Leverage) Nasil Calisir?
              </h2>
              
              <p className="text-slate-300 mb-4">
                Kaldirac, teminat (margin) olarak yatirilan sermayenin kati kadar pozisyon acmayi saglar.
              </p>
              
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <Card className="border-green-500/30 bg-green-950/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-green-400 font-bold text-2xl mb-1">2x - 5x</p>
                    <p className="text-slate-400 text-sm">Dusuk Risk</p>
                    <p className="text-slate-500 text-xs mt-1">Yeni baslayanlar icin</p>
                  </CardContent>
                </Card>
                <Card className="border-amber-500/30 bg-amber-950/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-amber-400 font-bold text-2xl mb-1">10x - 20x</p>
                    <p className="text-slate-400 text-sm">Orta Risk</p>
                    <p className="text-slate-500 text-xs mt-1">Deneyimli traderlar</p>
                  </CardContent>
                </Card>
                <Card className="border-red-500/30 bg-red-950/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-red-400 font-bold text-2xl mb-1">50x - 125x</p>
                    <p className="text-slate-400 text-sm">Cok Yuksek Risk</p>
                    <p className="text-slate-500 text-xs mt-1">Profesyonel traderlar</p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="flex items-start gap-3 text-slate-300 mb-4">
                <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <span>Kaldirac ne kadar yuksekse, likidasyon o kadar yakindir. 100x kaldiracta %1'lik ters hareket = tam sermaye kaybi.</span>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <XCircle className="h-4 w-4 text-red-400" />
                </div>
                Likidasyon Nedir?
              </h2>
              
              <p className="text-slate-300 mb-4">
                Likidasyon, kaldiracli pozisyonda zararin teminat miktarina esitlenmesidir. Bu durumda borsa pozisyonu zorla kapatir ve teminat kaybedilir.
              </p>
              
              <Card className="border-red-500/30 bg-red-950/20 mb-6">
                <CardContent className="p-4">
                  <p className="text-white font-medium mb-2">Ornek Likidasyon Senaryosu:</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-slate-300">
                      <span>Sermaye:</span>
                      <span className="text-white">$500</span>
                    </div>
                    <div className="flex justify-between text-slate-300">
                      <span>Kaldirac:</span>
                      <span className="text-amber-400">20x</span>
                    </div>
                    <div className="flex justify-between text-slate-300">
                      <span>Pozisyon:</span>
                      <span className="text-white">$10,000 LONG</span>
                    </div>
                    <div className="border-t border-slate-700/50 pt-2">
                      <div className="flex justify-between text-red-400">
                        <span>Likidasyon noktasi:</span>
                        <span className="font-medium">Fiyat ~%5 duserse</span>
                      </div>
                      <div className="flex justify-between text-red-400">
                        <span>Sonuc:</span>
                        <span className="font-medium">$500 tamamen kaybedilir</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-green-400" />
                </div>
                Futures'da Long ve Short
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <Card className="border-green-500/30 bg-green-950/20">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <TrendingUp className="h-6 w-6 text-green-400" />
                      <span className="font-bold text-white text-lg">LONG</span>
                    </div>
                    <p className="text-slate-300 text-sm mb-2">Fiyat yukselirse kar</p>
                    <p className="text-slate-400 text-xs">Yukselis trendi, destek bolgesi donus, bullish sinyal</p>
                  </CardContent>
                </Card>
                
                <Card className="border-red-500/30 bg-red-950/20">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <TrendingDown className="h-6 w-6 text-red-400" />
                      <span className="font-bold text-white text-lg">SHORT</span>
                    </div>
                    <p className="text-slate-300 text-sm mb-2">Fiyat duserse kar</p>
                    <p className="text-slate-400 text-xs">Dusus trendi, direnc bolgesi red, bearish sinyal</p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/rehber/long-nedir">
                  <Button variant="outline" size="sm" className="border-green-600 text-green-400 hover:bg-green-950">
                    Long Nedir? →
                  </Button>
                </Link>
                <Link to="/rehber/short-nedir">
                  <Button variant="outline" size="sm" className="border-red-600 text-red-400 hover:bg-red-950">
                    Short Nedir? →
                  </Button>
                </Link>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-red-400" />
                </div>
                Futures Trading Risk Yonetimi
              </h2>
              
              <p className="text-slate-300 mb-4">
                Futures islemlerde risk yonetimi hayati onem tasir:
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Her zaman Stop Loss kullan</strong> - Likidasyon oncesi pozisyonu koru</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Dusuk kaldirac sec</strong> - Baslangiçta 2x-5x kaldirac yeterlidir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Islem basina %2-5 risk</strong> - Toplam sermayenin kucuk bir kismini riske et</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Duygusal islem yapma</strong> - Plan disinda pozisyon acma</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-brand-green" />
                </div>
                Borsamex Futures Sinyalleri
              </h2>
              
              <p className="text-slate-300 mb-4">Borsamex AI sistemi futures sinyallerinde:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>200+ teknik gostergeyi analiz ederek yon belirler</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Giris, TP ve Stop seviyelerini otomatik hesaplar</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Risk/odul oranini optimize eder</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Hem LONG hem SHORT sinyaller uretir</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <Card className="border-amber-500/30 bg-amber-950/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="h-8 w-8 text-amber-400 shrink-0" />
                    <div>
                      <h3 className="text-lg font-bold text-amber-400 mb-2">Kritik Uyari</h3>
                      <p className="text-slate-300">
                        Futures trading cok yuksek risk icerir. Kaldiracli islemlerde sermayenin tamami kaybedilebilir. 
                        Bu icerik yatirim tavsiyesi degildir. Deneyimsiz kullanicilar icin futures islemler onerilmez.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BookOpen className="h-4 w-4 text-blue-400" />
                </div>
                Ilgili Makaleler
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/rehber/long-nedir" className="group">
                  <Card className="border-green-500/20 bg-slate-900/50 hover:border-green-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="h-5 w-5 text-green-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-green-400 transition-colors">Long Nedir?</p>
                          <p className="text-xs text-slate-500">Yukselis yonlu pozisyonlar</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/short-nedir" className="group">
                  <Card className="border-red-500/20 bg-slate-900/50 hover:border-red-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <TrendingDown className="h-5 w-5 text-red-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-red-400 transition-colors">Short Nedir?</p>
                          <p className="text-xs text-slate-500">Dusus yonlu pozisyonlar</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/tp-ve-stop-loss-nedir" className="group">
                  <Card className="border-green-500/20 bg-slate-900/50 hover:border-green-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Target className="h-5 w-5 text-green-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-green-400 transition-colors">TP ve Stop Loss Nedir?</p>
                          <p className="text-xs text-slate-500">Hedef ve risk seviyeleri</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/yeni-baslayanlar-icin-risk-yonetimi" className="group">
                  <Card className="border-amber-500/20 bg-slate-900/50 hover:border-amber-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Shield className="h-5 w-5 text-amber-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-amber-400 transition-colors">Risk Yonetimi</p>
                          <p className="text-xs text-slate-500">Sermaye koruma stratejileri</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Sik Sorulan Sorular</h2>
              <div className="space-y-4">
                {faqData.map((faq, idx) => (
                  <Card key={idx} className="border-slate-700/50 bg-slate-900/30">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-white mb-2 flex items-start gap-2">
                        <span className="text-brand-green">?</span>
                        {faq.question}
                      </h3>
                      <p className="text-slate-400 text-sm pl-5">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <ShareBox 
              title="Futures Trading Nedir? Kripto Vadeli Islemler Rehberi"
              url="/rehber/futures-trading-nedir"
              hashtags="#kripto #futures #trading #bitcoin"
            />

          </motion.div>

          <div className="flex items-center justify-between pt-8 border-t border-slate-800">
            <Link to="/rehber/short-nedir" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Short Nedir?
            </Link>
            <Link to="/rehber" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              Tum Rehberler
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-12 p-8 bg-gradient-to-r from-purple-900/20 to-brand-green/10 rounded-2xl border border-slate-700/50 text-center">
            <h3 className="text-xl font-bold text-white mb-3">
              Futures Trading Mantigini Ogrendin
            </h3>
            <p className="text-slate-400 mb-6">
              Simdi AI destekli futures sinyallerini takip et ve risk kontrollü islemleri incele.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ucretsiz Basla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

        </div>
      </article>
    </div>
  );
};

export default FuturesTradingNedirPage;
